<template>
  <div>
    <div class="field">
      <div class="control">
        <div class="select is-fullwidth">
          <select v-model="selectedItem" @change="itemSelected">
            <option v-for="option in options" :key="option.id" :value="option.id">{{ option.title }}</option>
          </select>
        </div>
      </div>
    </div>

    <div class="control">
      <n-button variant="primary" @click="complete()">Done</n-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ConditionOperationType, PickerOption } from '../models/conditions';
import { ConditionViewModel } from '../models/conditionViewModel';
import { required, requiredIf } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { PropType, ref, onMounted, computed } from 'vue';

const props = defineProps({
  condition: {
    type: Object as PropType<ConditionViewModel>,
    required: true,
  },
  options: {
    type: Array as PropType<PickerOption[]>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'changed', condition: ConditionViewModel): void;
  (e: 'completed', condition: ConditionViewModel): void;
}>();

const liveCondition = ref<ConditionViewModel>({
  attribute: '',
  operation: '',
  type: '',
  value: '',
  result: true,
});

const validations = computed(() => ({
  liveCondition: {
    value: {
      required: requiredIf(() => liveCondition.value.operation !== ConditionOperationType.IsSet),
    },
  },
}));

const $v = useVuelidate(validations, { liveCondition });

const selectedItem = ref('');

onMounted(() => {
  if (props.condition) {
    liveCondition.value = {
      attribute: props.condition.attribute,
      operation: ConditionOperationType.Equals,
      type: props.condition.type,
      value: props.condition.value,
      result: props.condition.result,
      unit: props.condition.unit,
      invalid: props.condition.invalid,
    };

    if (liveCondition.value.invalid) {
      $v.value.$touch();
    }
  }

  selectedItem.value = getSelectedItemValue();
  itemSelected();
});

function getSelectedItemValue() {
  if (!liveCondition.value || liveCondition.value.value.length === 0) {
    return props.options[0].id;
  }

  return liveCondition.value.value;
}

function itemSelected() {
  liveCondition.value.value = selectedItem.value;
  emitConditionChanged();
}

function emitConditionChanged() {
  if (props.condition.isNew) {
    props.condition.isNew = false;
  } else {
    liveCondition.value.invalid = $v.value.$invalid;
    emit('changed', liveCondition.value);
  }
}

function complete() {
  liveCondition.value.invalid = $v.value.$invalid;
  emit('completed', liveCondition.value);
}
</script>
